/**
 * ChatTranscript — the scrolling message list (Track 3 §5.2, T3-3, T3-5).
 *
 * ── LIVE-REGION DESIGN, AND WHY IT IS SHAPED THIS WAY ──────────────────────────────────────────
 * Track 3 raised two separate accessibility gaps against D9: the transcript had no `role="log"`
 * (T3-3), and per-file stage updates were never announced (T3-5). The obvious fix — a live region on
 * the transcript *and* another around the per-file stage block — is wrong: nested `aria-live`
 * regions double-announce in JAWS and NVDA, so the user would hear each stage twice.
 *
 * So there are exactly two announcement channels and they do not overlap:
 *   1. The scroll container is the ONLY live region in the subtree:
 *      `role="log" aria-live="polite" aria-relevant="additions text"`. `additions` announces new
 *      turns; `text` announces stage text changing inside an existing turn.
 *   2. One sr-only `role="status"` paragraph OUTSIDE the log carries a condensed per-file summary
 *      ("Preliminary SRS: Drafting changes. Project Plan: Computing the diff."). This is what makes
 *      progress legible without sight — a 3-file fan-out is otherwise a wall of near-identical
 *      announcements — and because it sits outside the log it cannot nest.
 *
 * ── AUTO-SCROLL ────────────────────────────────────────────────────────────────────────────────
 * Anchored to the bottom on new content, but suppressed once the user scrolls up, and re-armed when
 * they return to the bottom. Yanking a reader back down mid-sentence is the single most common
 * complaint about streaming chat UIs.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { cn } from "@/lib/utils";
import { ARTIFACT_LABELS, type ArtifactType } from "@/hooks/useArtifacts";
import {
  AGENT_STREAM_STAGE_LABELS,
  type ProjectChatMessage,
  type ProjectChatStreamState,
} from "@/hooks/useProjectChat";
import { MessageRow } from "./MessageRow";

/** Treat "within this many px of the bottom" as pinned to the bottom. */
const BOTTOM_THRESHOLD_PX = 48;

export interface ChatTranscriptProps {
  projectId: string;
  messages: ProjectChatMessage[];
  stream?: ProjectChatStreamState;
  isLoading: boolean;
  onResultResolved?: () => void;
  onOpenArtifact?: (artifactType: ArtifactType) => void;
  /**
   * Rendered inside the scroll container, above the message list (Item 2, §3.2).
   *
   * A NODE rather than the eight generation-state props, so this component stays a pure transcript
   * renderer. Its only requirement of the node is that it must NOT contain a live region — this
   * container is `role="log" aria-live="polite"` and a nested one double-announces.
   */
  systemPrompt?: React.ReactNode;
  /** Forwarded to every `ResultRow` — whether the viewer may accept / reject proposals. */
  canResolve?: boolean;
  className?: string;
}

/** One sentence per in-flight file, for the sr-only status summary. */
function buildProgressAnnouncement(
  stream: ProjectChatStreamState | undefined,
): string {
  if (!stream) return "";
  const perFile = Object.entries(stream.per_file ?? {});
  if (perFile.length === 0) {
    return stream.stage ? AGENT_STREAM_STAGE_LABELS[stream.stage] : "";
  }
  return perFile
    .map(
      ([artifactType, stage]) =>
        `${ARTIFACT_LABELS[artifactType as ArtifactType] ?? artifactType}: ${AGENT_STREAM_STAGE_LABELS[stage]}`,
    )
    .join(". ");
}

/**
 * Does the user turn at `index` have an agent turn?
 *
 * The agent row is written at `userSeq + 1` in `postTurn`'s transaction, so it is always the next
 * element of this chronological list when it exists. `seq` is compared as a number because the
 * column is `bigint` and arrives as a string on some paths.
 */
function hasAgentReply(messages: ProjectChatMessage[], index: number): boolean {
  const current = messages[index];
  if (current.role !== "user") return false;
  const next = messages[index + 1];
  return (
    next !== undefined &&
    next.role === "agent" &&
    Number(next.seq) === Number(current.seq) + 1
  );
}

export function ChatTranscript({
  projectId,
  messages,
  stream,
  isLoading,
  onResultResolved,
  onOpenArtifact,
  systemPrompt,
  canResolve = true,
  className,
}: ChatTranscriptProps): JSX.Element {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [pinnedToBottom, setPinnedToBottom] = useState(true);

  const handleScroll = useCallback((): void => {
    const el = scrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setPinnedToBottom(distanceFromBottom <= BOTTOM_THRESHOLD_PX);
  }, []);

  // Re-anchor on new turns and on stage changes, but only while the user has not scrolled away.
  const lastMessageId =
    messages.length > 0 ? messages[messages.length - 1].message_id : null;
  const streamSeq = stream?.seq ?? -1;
  useEffect(() => {
    if (!pinnedToBottom) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lastMessageId, streamSeq, pinnedToBottom]);

  const progressAnnouncement = useMemo(
    () => buildProgressAnnouncement(stream),
    [stream],
  );

  return (
    <>
      {/* Channel 2 — see the live-region note in this file's docstring. Outside the log on purpose. */}
      <p className="sr-only" role="status">
        {progressAnnouncement}
      </p>

      <div
        ref={scrollRef}
        onScroll={handleScroll}
        role="log"
        aria-live="polite"
        aria-relevant="additions text"
        aria-label="Project chat transcript"
        className={cn(
          "flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 py-4",
          className,
        )}
      >
        {/* Item 2 (§3.2) — the generation prompt sits ABOVE the message list but INSIDE this scroll
            container, so it scrolls away with history rather than pinning to the top forever. It is
            placed BEFORE the list so a keyboard user meets its action button first in the empty
            case (§3.6).

            It is a `role="note"`, deliberately not a `role="status"` or `role="alert"`: this
            container is the subtree's only live region, and a nested one double-announces in JAWS
            and NVDA. See `ChatSystemPrompt`'s `PromptShell`. */}
        {systemPrompt}

        {isLoading && (
          <p className="m-auto text-sm text-text-secondary">
            Loading conversation…
          </p>
        )}

        {!isLoading && messages.length === 0 && (
          <div className="m-auto max-w-md text-center">
            {/* 🚨 MUST NOT PROMISE QUESTION-ANSWERING — there is no backend path for it.
                An untagged message creates no agent row in `postTurn` (the pre-flight over an empty
                array returns `[]`), so the POST returns 202 and nothing ever happens. This is the
                first copy a new user reads, so promising Q&A here is the most expensive place to
                get it wrong.

                Item 5 (§6.6, AD-11) changes this wording — but only when `runAnswerTurn` and the
                `mode: 'answer'` queue variant actually ship. They have not. See the long comment on
                `ChatComposer`'s placeholder for the full reasoning and the five-surface checklist
                that must move together. */}
            <p className="text-sm font-medium text-text-primary">
              Tag the files you want to change
            </p>
            <p className="mt-1 text-sm text-text-secondary">
              Chat proposes edits to the files you tag — it does not answer
              untagged questions. Tagging two or more runs a cross-file
              consistency pass first.
            </p>
          </div>
        )}

        {messages.map((message, index) => (
          <MessageRow
            key={message.message_id}
            projectId={projectId}
            message={message}
            stream={
              stream?.message_id === message.message_id ? stream : undefined
            }
            onResultResolved={onResultResolved}
            onOpenArtifact={onOpenArtifact}
            canResolve={canResolve}
            // `postTurn` writes the agent row at `userSeq + 1` in the same transaction under the
            // session row lock, and `UNIQUE (session_id, seq)` forbids anything else taking that
            // slot — so the adjacency check is exact, not a heuristic. `MessageRow` needs it to
            // tell "nothing was editable" (no agent row at all) apart from a partial turn whose
            // skip rows sit here while its proposals sit on the agent row.
            agentReplyFollows={hasAgentReply(messages, index)}
          />
        ))}
      </div>
    </>
  );
}

export default ChatTranscript;
